import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, CheckCircle } from "@phosphor-icons/react";
import { MaskedLine, Overline, FadeUp } from "../components/Reveal";
import { AUDIENCES } from "../data/audiences";
import { SERVICES } from "../data/services";

export default function AudienceDetail() {
  const { slug } = useParams();
  const audience = AUDIENCES.find((a) => a.slug === slug);
  if (!audience) return <Navigate to="/" replace />;

  const related = SERVICES.filter((s) => audience.services.includes(s.slug));

  return (
    <>
      <section className="pt-32 lg:pt-40 pb-16" data-testid="audience-detail-hero">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp>
            <Link to="/" data-testid="audience-back-link" className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-accent transition-colors">
              <ArrowLeft size={16} /> Home
            </Link>
          </FadeUp>
          <div className="mt-8 grid gap-12 lg:grid-cols-12 items-end">
            <div className="lg:col-span-7">
              <FadeUp><Overline>Who We Serve — {audience.tagline}</Overline></FadeUp>
              <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter text-primary leading-[0.95]">
                <MaskedLine delay={0.1}>{audience.name}</MaskedLine>
              </h1>
              <FadeUp delay={0.35} className="mt-8 max-w-2xl">
                <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">{audience.description}</p>
              </FadeUp>
            </div>
            <div className="lg:col-span-5">
              <FadeUp delay={0.3}>
                <div className="overflow-hidden rounded-3xl border border-primary/10 aspect-[4/3]">
                  <img src={audience.image} alt={audience.name} className="h-full w-full object-cover" />
                </div>
              </FadeUp>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-white border-y border-primary/10" data-testid="audience-focus">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <span className="lg:sticky lg:top-32 block">
              <Overline>What we do for {audience.name}</Overline>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl font-medium tracking-tight text-primary">Focus areas</h2>
            </span>
          </div>
          <div className="lg:col-span-8 grid gap-4 sm:grid-cols-2">
            {audience.focus.map((f, i) => (
              <FadeUp key={f} delay={i * 0.05}>
                <div className="flex items-start gap-3 rounded-xl border border-primary/10 bg-background p-5 h-full">
                  <CheckCircle size={20} weight="fill" className="mt-0.5 shrink-0 text-accent" />
                  <p className="text-sm font-medium text-foreground leading-relaxed">{f}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28" data-testid="audience-services">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp><Overline>Relevant services</Overline></FadeUp>
          <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {related.map((s, i) => (
              <FadeUp key={s.slug} delay={i * 0.08}>
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`audience-service-${s.slug}`}
                  className="group flex h-full flex-col justify-between rounded-2xl border border-primary/10 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:border-accent/50"
                >
                  <h3 className="font-display text-xl font-medium tracking-tight text-foreground">{s.name}</h3>
                  <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-primary group-hover:text-accent transition-colors">
                    Explore <ArrowUpRight size={16} weight="bold" />
                  </span>
                </Link>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.2} className="mt-14">
            <Link
              to="/contact"
              data-testid="audience-contact-btn"
              className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-accent/20"
            >
              Talk to Us <ArrowUpRight size={16} weight="bold" />
            </Link>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
